import React, { useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { sanitizeCsvCellDisplay } from "../../../../utils/customCsvImport";

export interface CsvLayoutExampleTableProps {
  headers: string[];
  rows?: Array<Record<string, string>>;
  compact?: boolean;
  /** Pad with blank rows so single-row examples match multi-row table height in cards. */
  minBodyRows?: number;
  "aria-label"?: string;
}

function buildSyntheticRows(headers: string[]): Array<Record<string, string>> {
  if (!headers.length) return [];
  const row1: Record<string, string> = {};
  const row2: Record<string, string> = {};
  headers.forEach((h, i) => {
    row1[h] = i === 0 ? "1" : "…";
    row2[h] = i === 0 ? "2" : "…";
  });
  return [row1, row2];
}

function padRows(
  rows: Array<Record<string, string>>,
  headers: string[],
  minBodyRows: number
): Array<Record<string, string>> {
  if (minBodyRows <= rows.length) return rows;
  const padded = [...rows];
  while (padded.length < minBodyRows) {
    const blank: Record<string, string> = {};
    for (const h of headers) {
      blank[h] = "";
    }
    padded.push(blank);
  }
  return padded;
}

const CsvLayoutExampleTable = ({
  headers,
  rows,
  compact = false,
  minBodyRows = 1,
  "aria-label": ariaLabel = "Example CSV layout",
}: CsvLayoutExampleTableProps): React.ReactElement => {
  const displayRows = useMemo(() => {
    const base = rows?.length ? rows : buildSyntheticRows(headers);
    return padRows(base, headers, minBodyRows);
  }, [headers, rows, minBodyRows]);

  if (!headers.length) {
    return (
      <p className="font-[family-name:var(--font-primary)] text-xs font-normal text-gray-500">
        No example columns available.
      </p>
    );
  }

  return (
    <div
      data-shadcn-data-table
      data-csv-layout-example-table=""
      data-compact={compact ? "" : undefined}
      className={cn(
        "w-full overflow-hidden rounded-[6px] border border-border bg-background",
        compact && "[--data-table-header-height:2rem]"
      )}
    >
      <Table aria-label={ariaLabel}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {headers.map((header) => (
              <TableHead
                key={header}
                className={cn(compact && "text-xs font-normal")}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayRows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  className={cn(
                    "font-[family-name:var(--font-primary)] font-normal text-gray-500",
                    compact && "px-2 py-1.5 text-xs"
                  )}
                >
                  {sanitizeCsvCellDisplay(String(row[header] ?? ""))}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CsvLayoutExampleTable;
